// src/hooks/useXoePresence.ts
// The presence heartbeat (ClickUp 14ygtkj83tv S13): tells the clinic's xoe-agent which screen this
// practitioner is on, every 30 s and on a focus change, so a colleague's turn can say who is where.
// Mounted ONCE, in XoeFocusProvider — a second mount is a second heartbeat.
// Deployment gate (D32, no feature switch): Xoe/compliance/xoe-activity-timeline-position.

import { useCallback, useEffect, useRef } from "react";

import { sendXoePresence } from "../../api/xoe/XoePresenceApi";
import { isXoeAgentEnabled } from "../../api/xoe/xoeBaseUrl";
import type { XoeFocus } from "../../types/xoe/XoeFocusTypes";
import { xoePresencePayload } from "../../utils/xoe/pageContext";
import { can } from "../../utils/userPrefs/validatePermissions";
import { isSignedIn } from "../../lib/http/session";

// D34: the server key expires at 90 s, so three missed beats is "gone".
export const PRESENCE_HEARTBEAT_MS = 30_000;
// A navigation settles before it is reported; the 30 s tick still covers a missed one.
export const PRESENCE_FOCUS_DEBOUNCE_MS = 2_000;

const tabVisible = () => typeof document === "undefined" || document.visibilityState === "visible";

// A session holding xoe-agent's presence permission, not just a session: a patient's tab must never beat.
const practitionerSignedIn = () => isSignedIn() && can("xoe_agent.presence.write");

export const useXoePresence = (focus: XoeFocus, inConsult: boolean): void => {
	// The latest values, read at beat time — the interval must not restart on every focus change.
	const latest = useRef({ focus, inConsult });
	latest.current = { focus, inConsult };

	const beat = useCallback(() => {
		if (!isXoeAgentEnabled() || !tabVisible() || !practitionerSignedIn()) return;
		const { focus: current, inConsult: consult } = latest.current;
		sendXoePresence(xoePresencePayload(current, consult)).catch((error: unknown) => {
			// Silent by design: a heartbeat has no UI and nothing to retry. The message only —
			// an axios error carries the whole request config.
			console.debug("xoe presence heartbeat failed", error instanceof Error ? error.message : String(error));
		});
	}, []);

	useEffect(() => {
		if (!isXoeAgentEnabled()) return;
		beat();
		const interval = window.setInterval(beat, PRESENCE_HEARTBEAT_MS);
		const onVisibility = () => {
			if (tabVisible()) beat();
		};
		document.addEventListener("visibilitychange", onVisibility);
		return () => {
			window.clearInterval(interval);
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, [beat]);

	// The mount beat above already reported the initial focus; this reports CHANGES.
	const mounted = useRef(false);
	useEffect(() => {
		if (!mounted.current) {
			mounted.current = true;
			return;
		}
		if (!isXoeAgentEnabled()) return;
		const timer = window.setTimeout(beat, PRESENCE_FOCUS_DEBOUNCE_MS);
		return () => window.clearTimeout(timer);
	}, [focus.routeLabel, focus.sectionLabel, inConsult, beat]);
};
